import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import InfoCircle from "@/assets/info-circle.svg";
import stateData from "@/utils/stateData.json";

const StateTaxBenefits: React.FC = () => {
  const { t } = useTranslation();
  const [selectedState, setSelectedState] = useState<string>("");
  const [isMarried, setIsMarried] = useState<boolean>(false);

  const stateNames = stateData.states.map((s) => s.state);

  // Find selected state data
  const selectedStateData = stateData.states.find(
    (s) => s.state.toLowerCase() === selectedState.toLowerCase(),
  );

  // Helper to get deduction value
  const getDeduction = () => {
    if (!selectedStateData) return "-";
    const d = selectedStateData.deduction_or_credit;
    // Standard case: individual/joint
    if (d.individual && d.joint) {
      return isMarried ? d.joint : d.individual;
    }
    // Per beneficiary
    if (d.per_beneficiary_individual && d.per_beneficiary_joint) {
      return isMarried ? d.per_beneficiary_joint : d.per_beneficiary_individual;
    }
    if (d.per_beneficiary) {
      return d.per_beneficiary;
    }
    // In-state/out-state
    if (d.individual_in_state && d.joint_in_state) {
      return isMarried ? d.joint_in_state : d.individual_in_state;
    }
    // Deduction or Credit (Minnesota)
    if (d.individual_deduction && d.joint_deduction) {
      return isMarried ? d.joint_deduction : d.individual_deduction;
    }
    // Full contribution deductible
    if (d.full_contribution_deductible) {
      return "Full contribution deductible";
    }
    // Individual only
    if (d.individual) {
      return d.individual;
    }
    return "-";
  };

  // Helper to get credit value
  const getCredit = () => {
    if (!selectedStateData) return "-";
    const d = selectedStateData.deduction_or_credit;
    // Indiana: credit_rate & max_credit_usd
    if (d.credit_rate && d.max_credit_usd) {
      return `${d.credit_rate} up to $${d.max_credit_usd}`;
    }
    // Oregon: credit_flat
    if (d.credit_flat) {
      return isMarried ? d.credit_flat.joint : d.credit_flat.individual;
    }
    // Utah: credit_amount
    if (d.credit_amount) {
      return isMarried
        ? d.credit_amount.split("/")[1]?.trim()
        : d.credit_amount.split("/")[0]?.trim();
    }
    // Vermont: credit_rate
    if (d.credit_rate) {
      return d.credit_rate;
    }
    // Minnesota: max_credit
    if (d.max_credit) {
      return d.max_credit;
    }
    return "-";
  };

  return (
    <div className="">
      <div className="mt-[12px] mb-[24px]">
        <div className="bg-[#F8F8F8] py-[16px] px-[12px] rounded-[8px] border border-[#E2E2E2]">
          <div className="text-[#7D8688] text-[14px] flex space-x-2 items-center">
            <p>
              {t(
                "introduction_page.tax_section.state_benefits.your_state_label",
              )}
            </p>
            <img src={InfoCircle} alt="Info" />
          </div>
          <select
            className="w-full bg-[#F8F8F8] outline-none"
            name="state"
            id="state"
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
          >
            <option value="">
              {t(
                "introduction_page.tax_section.state_benefits.select_state_placeholder",
              )}
            </option>
            {stateNames.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-[24px] flex items-center space-x-2">
        <input
          type="checkbox"
          id="marital-status"
          name="marital-status"
          className="w-4 h-4 text-gray-400 border border-gray-400"
          checked={isMarried}
          onChange={() => setIsMarried(!isMarried)}
        />
        <label htmlFor="marital-status">
          {t(
            "introduction_page.tax_section.state_benefits.married_checkbox_label",
          )}
        </label>
      </div>

      <p className="mb-[12px]">
        <span className="font-semibold">
          {t(
            "introduction_page.tax_section.state_benefits.estimated_deduction_label",
          )}
        </span>{" "}
        {getDeduction()}
      </p>

      {getCredit() !== "-" && (
        <p className="mb-[12px]">
          <span className="font-semibold">
            {t(
              "introduction_page.tax_section.state_benefits.estimated_credit_label",
            )}
          </span>{" "}
          {getCredit()}
        </p>
      )}

      <p className="text-[#7D8688] text-[14px]">
        {t(
          "introduction_page.tax_section.state_benefits.tax_advisor_disclaimer",
        )}
      </p>
    </div>
  );
};

export default StateTaxBenefits;
